<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import ChartNoiseReduction from '$lib/PageSections/Charts/ChartNoiseReduction/ChartNoiseReduction.svelte';
	import CustomerStory from '$lib/PageSections/CustomerStory/CustomerStory.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import NoiseInAction from '$lib/PageSections/IndowInAction/NoiseInAction.svelte';
	import ProductRecommendation from '$lib/PageSections/Products/ProductRecommendation.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';
	import TrustedChoice from '$lib/PageSections/TrustedChoice/TrustedChoice.svelte';

	import { AcousticProduct } from '$lib/data/products/products-data.js';
	import { noiseReductionCustomerStory as customerStory } from '$routes/(public)/resources/case-studies/customer-stories.js';

	import windowDrapesImage from './images/windowDrapes.png';
	import yogaPoseWomanImage from './images/yogaPoseWoman.png';
</script>

<Section>
	<h2 class="text-left">The Treble With Windows</h2>
	<p class="text-left">
		Windows (even double paned) are notoriously bad insulators - especially
		against noise. Whether its dogs barking, cars driving by, construction, or
		any of the other noisy nuisances we encounter in our neighborhoods, windows
		can make it seem like the sound is coming from inside the house.
	</p>
	<Image src={yogaPoseWomanImage} loading="eager" />
	<p class="text-left">
		Soundproofing your home can be a challenge. Window replacement is an option
		for some, but can be a very costly and intrusive process and often only
		reduce noise by 25%. The total costs of window replacements (including
		labor) <a href="/how-to/increase-home-resale-value"
			>aren't a great option for increasing your home's resale value</a
		>.
	</p>
</Section>

<Section backgroundColor="grey-light">
	<h3 class="text-indow-grey-dark">Indow vs Replacement Windows</h3>
	<p>
		Adding Indow inserts to an existing window can make your windows perform as
		well (or better!) than a replacement window, but at a fraction of the cost
		and without the hassle and disruption of installing new windows.
	</p>
	<ChartNoiseReduction />
</Section>

<Section>
	<p class="text-left">
		Other 'soundproof' options - like heavy blinds or 'sound absorbing' curtains
		- will only dampen outside noise, but also block out the things your windows
		do best - bathe your home in natural light. This is where window inserts
		really shine.
	</p>

	<Image src={windowDrapesImage} />

	<h2 class="text-left">
		Superior Noise Reduction with Indow Acoustic Inserts
	</h2>

	<p class="text-left">
		With our patented Snug Fit compression tubing and premium acrylic glazing,
		our <a href="/products/acoustic-window-inserts"
			>Acoustic Grade Window Inserts</a
		> can block up to 70% of the noise coming through your windows. That's about as
		close as you can get to soundproofing your windows without building a wall over
		them.
	</p>
	<p class="text-left">
		But the best part? They blend in with your existing window frames and let
		light flow in freely. In fact, if it wasn't for how much quieter they make
		your home - you'd forget they were there!
	</p>

	<NoiseInAction />
</Section>

<CustomerStory story={customerStory} />

<Section>
	<h2 class="text-left">More Quiet, More Comfort, More Savings</h2>
	<p class="text-left">
		In addition to a substantial reduction in noise, <a
			href="/products/acoustic-window-inserts">Indow Acoustic Inserts</a
		> also provide significant thermal insulating properties, keeping your home warmer
		in winter, cooler in summer, and realizing an average 20% in energy savings. It's
		a win-win-win for your pocket book, the planet, and your day-to-day comfort.
	</p>
	<p class="text-left">
		Our air-tight seal not only keeps noise out and your ideal temperature in,
		but it also <a href="/solutions/prevent-window-condensation"
			>reduces condensation</a
		>
		and
		<a href="/solutions/block-pollen-and-pollutants"
			>blocks dust, pollen, and other airborne particles</a
		>. But that doesn't mean you'll have to live in a hermetically sealed home!
		With a gentle tug on the provided D-ring, you can break the seal and remove
		the inserts whenever you want to open your windows and let in some fresh
		air.
	</p>
</Section>

<ProductRecommendation product={AcousticProduct} />

<TrustedChoice />
<Reviews />
<GetAFreeEstimate />
